/* App shell: left rail (desktop), top bar + bottom dock (phone), routed main column, toasts, dialogs. */
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { APP_VERSION } from '../content/changelog';
import { levelInfo, streak, useProgress, useSettings } from '../core/store';
import { scrollTop } from '../core/util';
import { Icon, type IconName, Logo } from '../ui/icons';
import { Modal } from '../ui/Modal';
import { Toaster } from '../ui/Toaster';
import { ProgressBar } from '../ui/widgets';
import { MicChip } from './MicChip';
import { MicSetupDialog } from './MicSetup';
import { fromPath, toPath, useGo } from './navigation';
import { useMicSetupOpen } from './uiState';

interface NavEntry {
  id: string;
  label: string;
  icon: IconName;
  mod?: boolean;
  short?: string;
}

const NAV: readonly NavEntry[] = [
  { id: 'ana', label: 'Ana sayfa', icon: 'home', short: 'Ana' },
  { id: 'gitar', label: 'Duyduğunu Çal', icon: 'pick', mod: true, short: 'Çal' },
  { id: 'nota', label: 'Nota Tanıma', icon: 'note', mod: true, short: 'Nota' },
  { id: 'vokal', label: 'Vokal & Şan', icon: 'voice', mod: true, short: 'Vokal' },
  { id: 'akor', label: 'Akor Kulağı', icon: 'chordbox', mod: true, short: 'Akor' },
  { id: 'jam', label: 'Jam', icon: 'jam' },
  { id: 'araclar', label: 'Araçlar', icon: 'tuner' },
  { id: 'az-bilinenler', label: 'Az Bilinenler', icon: 'bulb' },
  { id: 'ilerleme', label: 'İlerleme', icon: 'chart' },
  { id: 'ayarlar', label: 'Ayarlar', icon: 'sliders' },
];
const DOCK = ['ana', 'gitar', 'nota', 'vokal', 'akor'];

function Brand() {
  return (
    <Link className="brand" to="/">
      <Logo />
      <span className="brand-name">
        kulaktan<span className="brand-tld">.com</span>
      </span>
    </Link>
  );
}

function NavIcon({ entry }: { entry: NavEntry }) {
  return (
    <span className={`nav-ic${entry.mod ? ` mod-${entry.id}` : ''}`}>
      <Icon name={entry.icon} />
    </span>
  );
}

function RailStreak() {
  const progress = useProgress();
  const lv = levelInfo();
  return (
    <div className="rail-streak">
      <div className="rs-row">
        <span className="rs-fl">
          <Icon name="flame" />
        </span>
        <span>{streak()} gün seri</span>
        <span className="rs-sp" />
        <span className="rs-xp">{progress.xp} XP</span>
      </div>
      <div className="rs-lv">
        <span>{lv.name}</span>
        <ProgressBar frac={lv.frac} />
      </div>
    </div>
  );
}

function MenuDialog({ onClose }: { onClose: () => void }) {
  const go = useGo();
  const [closed, setClosed] = useState(false);
  if (closed) return null;
  return (
    <Modal title="Menü" onClose={onClose}>
      <div className="menu-list">
        {NAV.map((n) => (
          <button
            key={n.id}
            type="button"
            className="menu-item"
            onClick={() => {
              setClosed(true);
              onClose();
              go(n.id);
            }}
          >
            <NavIcon entry={n} />
            <span>{n.label}</span>
          </button>
        ))}
      </div>
    </Modal>
  );
}

const isExerciseRoute = (id: string) => /\.egz\.|\.meydan\./.test(id);

export function Shell() {
  const { pathname } = useLocation();
  const routeId = fromPath(pathname);
  const top = routeId.split('.')[0];
  const settings = useSettings();
  const micSetup = useMicSetupOpen();
  const [menu, setMenu] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll to top whenever the page changes
  useEffect(() => scrollTop(), [pathname]);

  // Note names are computed when a page renders, so a naming change re-mounts the page
  // (except inside a running exercise, which keeps its state).
  const naming = `${settings.noteNames}|${settings.accidental}`;
  const viewKey = isExerciseRoute(routeId) ? pathname : `${pathname}|${naming}`;

  return (
    <div className="shell">
      <aside className="rail">
        <Brand />
        <nav className="nav" aria-label="Bölümler">
          {NAV.map((n) => (
            <Link key={n.id} className={`nav-item${top === n.id ? ' on' : ''}`} to={toPath(n.id)} data-route={n.id}>
              <NavIcon entry={n} />
              <span>{n.label}</span>
            </Link>
          ))}
        </nav>
        <div className="rail-foot">
          <MicChip />
          <RailStreak />
        </div>
      </aside>
      <div className="col">
        <header className="topbar">
          <Brand />
          <div className="tb-sp" />
          <MicChip compact />
          <button type="button" className="icon-btn" aria-label="Menü" title="Menü" onClick={() => setMenu(true)}>
            <Icon name="menu" />
          </button>
        </header>
        <main id="main" className="main" tabIndex={-1}>
          <Outlet key={viewKey} />
        </main>
        <footer className="site-foot">
          <span>kulaktan.com · gitar ve vokal için kulak eğitimi</span>
          <span>Mikrofon sesi kaydedilmez; yalnızca tarayıcında anlık analiz edilir.</span>
          <Link className="foot-ver" to={toPath('yenilikler')}>
            Sürüm {APP_VERSION} · Yenilikler
          </Link>
        </footer>
      </div>
      <nav className="dock" aria-label="Hızlı geçiş">
        {DOCK.map((id) => {
          const n = NAV.find((x) => x.id === id) as NavEntry;
          return (
            <Link key={id} className={`dock-item${top === id ? ' on' : ''}`} to={toPath(id)} data-route={id}>
              <NavIcon entry={n} />
              <span>{n.short}</span>
            </Link>
          );
        })}
      </nav>
      <Toaster />
      {micSetup ? <MicSetupDialog /> : null}
      {menu ? <MenuDialog onClose={() => setMenu(false)} /> : null}
    </div>
  );
}
